import { useEffect, useLayoutEffect, useState } from 'react';
import { ArrowRight, X } from 'lucide-react';

/**
 * First-visit guided tour. A welcome step plus four spotlighted steps that
 * dim the page (SVG mask) and cut a hole around the real element via
 * `data-tour="..."` attributes, with a tooltip placed next to the spotlight.
 *
 * Activation is client-side and one-time:
 *   - The `/welcome` "Get started" handoff sets `unify.tour-pending`, so the
 *     tour auto-starts the first time the dashboard mounts after onboarding.
 *   - Anything firing a `tour:start` window event also starts it (for a future
 *     "replay tour" affordance).
 * Skipping or finishing writes `unify.tour-completed` so it never re-triggers.
 *
 * Add a step by putting `data-tour="my-step"` on the target and adding it to
 * STEPS below.
 */
const STEPS = [
	{
		title: 'Welcome to Unify!',
		body: "Let's take a quick 4-step look around, so you're set up for success.",
		placement: 'center'
	},
	{
		selector: '[data-tour="sidebar-nav"]',
		title: 'Your control center',
		body: 'Jump between Orders, Ads, Channels, Inventory and Results from here.',
		placement: 'right'
	},
	{
		selector: '[data-tour="ai-chat"]',
		title: 'Ask AI anything',
		body: 'Get help, insights and quick actions from your AI assistant — no leaving the page.',
		placement: 'right'
	},
	{
		selector: '[data-tour="local-potential"]',
		title: 'Your Local Potential score',
		body: 'See how much more you can do to grow your local sales and revenue — the higher the score, the more shoppers you reach.',
		placement: 'left'
	},
	{
		selector: '[data-tour="quick-wins"]',
		title: 'Actions to take right away',
		body: "Some immediate steps to get the most out of Unify and your store's performance.",
		placement: 'top',
		noScroll: true
	}
];

const TOUR_COMPLETED_KEY = 'unify.tour-completed';
const TOUR_PENDING_KEY = 'unify.tour-pending';
const PAD = 8;
const EDGE_MARGIN = 16;
const TIP_WIDTH = 320;

const read = (key) => {
	try {
		return typeof window !== 'undefined' && window.localStorage.getItem(key) === '1';
	} catch {
		return false;
	}
};

const clearKey = (key) => {
	try {
		window.localStorage.removeItem(key);
	} catch {
		/* ignore */
	}
};

export function IntroTour() {
	const [active, setActive] = useState(false);
	const [step, setStep] = useState(0);
	const [rect, setRect] = useState(null);
	const [visible, setVisible] = useState(false);

	const current = STEPS[step];

	// Auto-start once: the welcome hand-off (or a manual `tour:start`) opens it,
	// unless the user has already completed/skipped it.
	useEffect(() => {
		// Don't clear the pending flag here — the initial `/` → `/:shopId` redirect
		// remounts this component, and clearing on the first mount would stop the
		// tour ever showing. The flag is cleared on finish/skip instead.
		const start = () => {
			if (read(TOUR_COMPLETED_KEY)) return;
			setStep(0);
			setActive(true);
		};
		if (read(TOUR_PENDING_KEY)) start();
		window.addEventListener('tour:start', start);
		return () => window.removeEventListener('tour:start', start);
	}, []);

	// Broadcast the tour's running state so the LaunchHighlight (a sibling
	// overlay) can suppress itself while the tour is on screen, then show the
	// "Launch your Local Storefront" focus once the tour finishes OR is skipped.
	useEffect(() => {
		try {
			window.dispatchEvent(new CustomEvent('unify:tour-active', { detail: active }));
		} catch {
			/* best-effort hand-off */
		}
	}, [active]);

	// Padded spotlight rect, clamped so it never crosses the viewport edge.
	const spot = rect
		? (() => {
				const x1 = Math.max(EDGE_MARGIN, rect.left - PAD);
				const y1 = Math.max(EDGE_MARGIN, rect.top - PAD);
				const x2 = Math.min(window.innerWidth - EDGE_MARGIN, rect.right + PAD);
				const y2 = Math.min(window.innerHeight - EDGE_MARGIN, rect.bottom + PAD);
				return { left: x1, top: y1, width: Math.max(0, x2 - x1), height: Math.max(0, y2 - y1), right: x2, bottom: y2 };
			})()
		: null;

	useLayoutEffect(() => {
		if (!active) return undefined;
		if (!current?.selector) {
			setRect(null);
			return undefined;
		}
		const update = () => {
			const el = document.querySelector(current.selector);
			setRect(el ? el.getBoundingClientRect() : null);
		};
		const el = document.querySelector(current.selector);
		if (!current.noScroll) el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		update();
		const t = setTimeout(update, 50);
		window.addEventListener('resize', update);
		window.addEventListener('scroll', update, true);
		return () => {
			clearTimeout(t);
			window.removeEventListener('resize', update);
			window.removeEventListener('scroll', update, true);
		};
	}, [active, step, current?.selector, current?.noScroll]);

	useEffect(() => {
		if (!active) {
			setStep(0);
			setVisible(false);
			return undefined;
		}
		const t = setTimeout(() => setVisible(true), 800);
		return () => clearTimeout(t);
	}, [active]);

	if (!active || !current) return null;

	const finish = () => {
		try {
			window.localStorage.setItem(TOUR_COMPLETED_KEY, '1');
		} catch {
			/* ignore */
		}
		clearKey(TOUR_PENDING_KEY);
		setActive(false);
	};
	const next = () => (step >= STEPS.length - 1 ? finish() : setStep(step + 1));
	const back = () => setStep(Math.max(0, step - 1));

	const EDGE = 24;
	let tipStyle = { top: EDGE, left: EDGE };
	if (current.placement === 'center') {
		tipStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
	} else if (spot) {
		const clampTop = (t) => Math.max(EDGE, Math.min(window.innerHeight - 240, t));
		const clampLeft = (l) => Math.max(EDGE, Math.min(window.innerWidth - TIP_WIDTH - EDGE, l));
		if (current.placement === 'right') {
			tipStyle = { top: clampTop(spot.top), left: clampLeft(spot.right + 16) };
		} else if (current.placement === 'left') {
			tipStyle = { top: clampTop(spot.top), left: clampLeft(spot.left - TIP_WIDTH - 16) };
		} else if (current.placement === 'top') {
			tipStyle = { top: clampTop(spot.top - 220 - 16), left: clampLeft(spot.left - 80) };
		} else {
			tipStyle = { top: clampTop(spot.bottom + 16), left: clampLeft(spot.left) };
		}
	}

	return (
		<div
			className={`pointer-events-none fixed inset-0 z-[60] transition-opacity duration-300 ease-out ${visible ? 'opacity-100' : 'opacity-0'}`}
		>
			<svg className="pointer-events-auto absolute inset-0 h-full w-full">
				<defs>
					<mask id="intro-tour-mask">
						<rect x="0" y="0" width="100%" height="100%" fill="white" />
						{spot ? (
							<rect x={spot.left} y={spot.top} width={spot.width} height={spot.height} rx={14} ry={14} fill="black" />
						) : null}
					</mask>
				</defs>
				<rect x="0" y="0" width="100%" height="100%" fill="hsl(var(--foreground) / 0.55)" mask="url(#intro-tour-mask)" />
			</svg>

			{spot ? (
				<div
					className="pointer-events-none absolute rounded-[14px] ring-2 ring-primary shadow-[0_0_0_6px_hsl(var(--primary)/0.25)] transition-all duration-200"
					style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height }}
				/>
			) : null}

			<div
				className="pointer-events-auto absolute w-[320px] rounded-2xl bg-gradient-unify p-5 text-white shadow-2xl ring-1 ring-white/15"
				style={tipStyle}
			>
				<div className="mb-2 flex items-center justify-between">
					{step > 0 ? (
						<span className="rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-white">
							Step {step} of {STEPS.length - 1}
						</span>
					) : (
						<span />
					)}
					<button
						type="button"
						onClick={finish}
						aria-label="Skip quick tour"
						className="text-white/60 transition hover:text-white"
					>
						<X className="h-4 w-4" />
					</button>
				</div>
				<h3 className="text-base font-extrabold tracking-tight text-white">{current.title}</h3>
				<p className="mt-1.5 text-sm text-white/75">{current.body}</p>

				{step > 0 ? (
					<div className="mt-4 flex items-center gap-1.5">
						{STEPS.slice(1).map((s, i) => (
							<span
								key={s.selector}
								className={`h-1.5 rounded-full transition-all ${i === step - 1 ? 'w-6 bg-white' : 'w-1.5 bg-white/25'}`}
							/>
						))}
					</div>
				) : null}

				<div className="mt-5 flex items-center justify-between gap-3">
					<button type="button" onClick={finish} className="text-xs text-white/60 transition hover:text-white">
						Skip
					</button>
					<div className="flex items-center gap-2">
						{step > 0 ? (
							<button
								type="button"
								onClick={back}
								className="rounded-full px-3 py-1.5 text-xs font-semibold text-white/80 transition hover:bg-white/10"
							>
								Back
							</button>
						) : null}
						<button
							type="button"
							onClick={next}
							className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-1.5 text-xs font-semibold text-primary transition hover:bg-white/90"
						>
							{step === STEPS.length - 1 ? 'Finish' : step === 0 ? 'Start quick tour' : 'Next'}{' '}
							<ArrowRight className="h-3.5 w-3.5" />
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
